import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import { fmtDollar, Select, Btn } from '../components/UI';
import Icon from '../components/Icon';
import { useStore, useFieldOptions } from '../data/store';
import { useAuth } from '../data/auth';
import FieldEditor from '../components/FieldEditor';
import AddContactModal from '../components/contacts/AddContactModal';
import { EditActivityModal } from '../components/EditActivityModal';

const cfDateTime = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); let h = dt.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; const mn = String(dt.getMinutes()).padStart(2,"0"); return m+"/"+dy+"/"+y+" "+h+":"+mn+" "+ap; };
const cfDate = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); return m+"/"+dy+"/"+y; };

export default function DealRecord({ dealId, onBack, onSelectContact, isMobile }) {
  const store = useStore();
  const auth = useAuth();
  // Tier B options — Deal Source reuses the contacts.source list (lead source
  // is conceptually shared across contact + deal records).
  const sourceOptions = useFieldOptions("contacts", "source");
  const { deals, contacts, tasks: allTasks, updateDealStage, addNote: storeAddNote, addTask: storeAddTask, getTasksForDeal, stages: STAGES, stageColors: STAGE_COLORS } = store;
  const rawDeal = deals.find(d => d.id === dealId);
  const deal = rawDeal ? { risk: "low", close: "", created: "", ai: "", properties: {}, owner: "", co: "", ct: "", name: "", val: 0, stage: "Discovery", prob: 0, days: 0, ...rawDeal } : null;
  const contact = contacts.find(c => c.id === deal?.ct);
  const relatedDeals = deals.filter(d => d.ct === deal?.ct && d.id !== dealId);
  const tasks = getTasksForDeal(dealId);
  const [activeTab, setActiveTab] = useState("activity");
  const [noteText, setNoteText] = useState("");
  const [showAddContact, setShowAddContact] = useState(false);
  const [showCreateContactModal, setShowCreateContactModal] = useState(false);
  const [hoveredContactId, setHoveredContactId] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [quickAction, setQuickAction] = useState(null);
  const [qaText, setQaText] = useState("");
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Stage-gate note prompt: opens when updateDealStage returns "require_note"
  // (i.e., target stage has requireStageNote: true and no note was provided yet).
  const [stageNoteModal, setStageNoteModal] = useState(null); // { dealId, newStage }
  const [editingActivity, setEditingActivity] = useState(null);
  const [stageNoteText, setStageNoteText] = useState("");
  const actionsMenuRef = React.useRef(null);

  const canDeleteThisDeal =
    !!deal && (deal.owner_id === auth.currentUser?._dbId || auth.hasPermission("deals_delete"));
  const canEditThisDeal =
    !!deal && (deal.ownerId === auth.currentUser?._dbId || auth.hasPermission("deals_edit"));

  const submitStageNote = async () => {
    if (!stageNoteModal || !stageNoteText.trim()) return;
    await updateDealStage(stageNoteModal.dealId, stageNoteModal.newStage, stageNoteText.trim());
    setStageNoteModal(null);
    setStageNoteText("");
  };

  const confirmDeleteDeal = async () => {
    if (!deal?.id) return;
    setDeleteBusy(true);
    try {
      await store.deleteDeal(deal.id);
      setShowDeleteModal(false);
      if (onBack) onBack();
    } finally {
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    if (!showActionsMenu) return;
    const handler = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) setShowActionsMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showActionsMenu]);

  // Reset picker search when the modal closes so next open starts fresh
  useEffect(() => { if (!showAddContact) setPickerSearch(""); }, [showAddContact]);

  if (!deal) return <div style={{ padding: 40, color: T.txt }}>Deal not found</div>;

  const currentStage = deal.stage;
  const stageIdx = STAGES.indexOf(currentStage);
  const dealContactRows = store.getContactsForDeal(deal.id);
  const dealOwner = store.getOwnerName(deal.owner) !== "—" ? store.getOwnerName(deal.owner) : (contact ? store.getOwnerName(contact.owner) : store.teamMembers[0]?.name || "Unassigned");
  const availableContacts = contacts.filter(c => !dealContactRows.some(r => r.contact.id === c.id));

  // Activity feed reads only from store.activities — single source of truth.
  // Filter unions deal-linked rows + rows linked to any contact in this deal,
  // so we catch both direct deal activity and contact-side notes/calls/etc
  // without double-counting (each activity row appears once via its single id).
  const dealContactIds = new Set((dealContactRows || []).map(r => r.contact?.id).filter(Boolean));
  const activities = store.activities
    .filter(a => a.dealId === dealId || (a.contactId && dealContactIds.has(a.contactId)))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const tabFilter = { activity: "all", notes: "note", emails: "email", calls: "call", tasks: "task", meetings: "meeting" };

  const saveNote = () => {
    if (!noteText.trim()) return;
    // store.addNote prepends to store.activities synchronously, so the row
    // appears in the feed immediately — no local optimistic state needed.
    storeAddNote({ contactId: deal.ct, dealId, title: "Note on deal: " + deal.name, notes: noteText });
    setNoteText("");
  };

  const submitQuickAction = () => {
    if (!qaText.trim()) return;
    // Persist via store-side helpers so quick-action entries survive reload
    // and show up in the activity feed via store.activities (not just local
    // session state). Each helper threads through addActivity → DB and
    // prepends to store.activities synchronously for instant feed updates.
    if (quickAction === "Note")    store.addNote({ contactId: deal.ct, dealId, title: "Note logged", notes: qaText });
    if (quickAction === "Call")    store.logCall({ contactId: deal.ct, dealId, title: qaText, notes: qaText, duration: 0 });
    if (quickAction === "Email")   store.logEmail({ contactId: deal.ct, dealId, title: qaText, notes: qaText });
    if (quickAction === "Meeting") store.logMeeting({ contactId: deal.ct, dealId, title: "Meeting logged", notes: qaText });
    if (quickAction === "Task")    store.addTask({ title: qaText, pri: "medium", due: cfDate(new Date(Date.now() + 3 * 86400000)), ct: deal.ct, dealId, assignee: dealOwner, done: false });
    setQaText(""); setQuickAction(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* ─── CHEVRON STAGE BAR ─── */}
      <div style={{ display: "flex", flexShrink: 0, height: 36, background: "transparent", margin: "8px 24px 0" }}>
        {STAGES.map((s, i) => {
          const isLast = i === STAGES.length - 1;
          const isCurrent = i === stageIdx;
          const isPast = i < stageIdx;
          const bg = isCurrent ? "#4059AD" : "#2C2E33";
          const txtColor = "#FAFAFA";
          const strokeColor = "rgba(217,219,221,0.25)";
          return (
            <div key={s} onClick={async () => {
              const result = await updateDealStage(dealId, s);
              if (result === "require_note") {
                setStageNoteModal({ dealId, newStage: s });
                setStageNoteText("");
              }
              // No local setNotes prepend: updateDealStage's internal activity
              // log creates the lifecycle entry when the move actually succeeds.
              // Prepending unconditionally previously showed phantom "Moved to X"
              // entries even when the gate rejected the move.
            }} style={{ flex: 1, position: "relative", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg viewBox="0 0 200 36" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <path d={i === 0 ? "M0,0 L185,0 L200,18 L185,36 L0,36 Z" : isLast ? "M0,0 L200,0 L200,36 L0,36 L15,18 Z" : "M0,0 L185,0 L200,18 L185,36 L0,36 L15,18 Z"} fill={bg} stroke={strokeColor} strokeWidth="1" />
              </svg>
              <span style={{ position: "relative", zIndex: 1, fontSize: 11, fontWeight: 400, color: txtColor }}>{s}</span>
            </div>
          );
        })}
      </div>

      {/* ─── THREE COLUMN LAYOUT ─── */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", flex: 1, overflow: isMobile ? "auto" : "hidden" }}>

        {/* LEFT */}
        <div style={{ width: isMobile ? "100%" : 280, flexShrink: 0, borderRight: isMobile ? "none" : "1px solid rgba(217,219,221,0.08)", borderBottom: isMobile ? "1px solid rgba(217,219,221,0.08)" : "none", overflowY: isMobile ? "visible" : "auto", padding: isMobile ? 12 : 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button onClick={onBack} style={{ background: "none", border: "none", color: "#FAFAFA", fontSize: 11, fontWeight: 400, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontFamily: "inherit" }}>
              <Icon name="chevronL" size={14} color="#FAFAFA" /> Deals
            </button>
            {canDeleteThisDeal && (
              <div ref={actionsMenuRef} style={{ position: "relative" }}>
                <button onClick={e => { e.stopPropagation(); setShowActionsMenu(v => !v); }} style={{ padding: "2px 6px", borderRadius: 3, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Actions ▾</button>
                {showActionsMenu && (
                  <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, minWidth: 160, background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 20, padding: 4 }}>
                    <button onClick={() => { setShowActionsMenu(false); setShowDeleteModal(true); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", fontSize: 11, background: "transparent", border: "none", color: "#E53935", cursor: "pointer", borderRadius: 4, fontFamily: "inherit" }}>Delete deal</button>
                  </div>
                )}
              </div>
            )}
          </div>

          <FieldEditor
            label="Deal name"
            value={deal.name}
            type="text"
            readOnly={!canEditThisDeal}
            onSave={v => store.updateDeal(dealId, { name: v })}
            renderDisplay={v => <div style={{ fontSize: 16, fontWeight: 400, color: T.txt, marginBottom: 2, cursor: canEditThisDeal ? "pointer" : "default" }}>{v || "Untitled deal"}</div>}
          />
          <div style={{ fontSize: 11, color: T.txt, opacity: 0.5, marginBottom: 8 }}>{deal.co}</div>
          <FieldEditor
            label="Amount"
            value={deal.val}
            type="currency"
            prefix="$"
            readOnly={!canEditThisDeal}
            onSave={v => store.updateDeal(dealId, { val: Number(v) || 0 })}
            renderDisplay={v => <div style={{ fontSize: 28, fontWeight: 400, color: "#2E8B57", marginBottom: 10, cursor: canEditThisDeal ? "pointer" : "default" }}>{fmtDollar(v)}</div>}
          />

          {/* Status inline */}
          <div style={{ display: "flex", gap: 12, fontSize: 11, color: T.txt, marginBottom: 14 }}>
            <span><span style={{ opacity: 0.4 }}>Stage</span> <span style={{ fontWeight: 400 }}>{currentStage}</span></span>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ opacity: 0.4 }}>Risk</span>
              <FieldEditor
                label="Risk"
                value={deal.risk || "low"}
                type="select"
                options={["none", "low", "medium", "high"]}
                readOnly={!canEditThisDeal}
                onSave={v => store.updateDeal(dealId, { risk: v })}
                renderDisplay={v => <span style={{ fontWeight: 400, textTransform: "capitalize", cursor: canEditThisDeal ? "pointer" : "default" }}>{v}</span>}
              />
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ opacity: 0.4 }}>Prob</span>
              <FieldEditor
                label="Probability"
                value={deal.prob}
                type="number"
                suffix="%"
                readOnly={!canEditThisDeal}
                onSave={v => store.updateDeal(dealId, { prob: Number(v) || 0 })}
                renderDisplay={v => <span style={{ fontWeight: 400, cursor: canEditThisDeal ? "pointer" : "default" }}>{v}%</span>}
              />
            </div>
          </div>

          {/* About */}
          <div style={{ fontSize: 10, fontWeight: 400, color: T.txt, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 }}>About this deal</div>
          <FieldEditor
            label="Owner"
            value={deal.ownerId || deal.owner || ""}
            type="select"
            options={store.teamMembers.map(m => ({ value: m._dbId, label: m.name }))}
            readOnly={!canEditThisDeal}
            onSave={v => store.updateDeal(dealId, { owner: v })}
          />
          <FieldEditor
            label="Close date"
            value={deal.close || ""}
            type="date"
            readOnly={!canEditThisDeal}
            onSave={v => store.updateDeal(dealId, { close: v })}
          />
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 11, borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
            <span style={{ color: T.txt, opacity: 0.4 }}>Days in stage</span><span style={{ color: T.txt, fontWeight: 400 }}>{(deal.days || 0) + " days"}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 11, borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
            <span style={{ color: T.txt, opacity: 0.4 }}>Source (contact)</span><span style={{ color: T.txt, fontWeight: 400 }}>{contact?.source || "—"}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 11, borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
            <span style={{ color: T.txt, opacity: 0.4 }}>Created</span><span style={{ color: T.txt, fontWeight: 400 }}>{deal.created || "—"}</span>
          </div>

          {/* Deal Details */}
          <div style={{ fontSize: 10, fontWeight: 400, color: T.txt, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8, marginTop: 14 }}>Deal Details</div>
          <FieldEditor
            label="Deal Source"
            value={deal.properties?.source || ""}
            type="select"
            options={sourceOptions}
            readOnly={!canEditThisDeal}
            onSave={v => store.updateDeal(dealId, { source: v })}
          />

          {/* Custom fields */}
          {(store.customFields?.deals || []).map(f => {
            if (f.type === "calculation") {
              let val = 0;
              try { let formula = f.formula || ""; Object.keys(deal).forEach(k => { if (typeof deal[k] === "number") formula = formula.replace(new RegExp("\\b" + k + "\\b", "g"), deal[k]); }); val = Function('"use strict"; return (' + formula.replace(/[^0-9+\-*/().,%\s]/g, "") + ')')(); if (isNaN(val)) val = 0; } catch { val = 0; }
              const display = f.calcFormat === "currency" ? "$" + Number(val).toLocaleString() : f.calcFormat === "percent" ? Number(val).toFixed(1) + "%" : Number(val).toLocaleString();
              return <div key={f.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 11, borderBottom: "1px solid rgba(217,219,221,0.06)" }}><span style={{ color: T.txt, opacity: 0.4 }}>{f.label}</span><span style={{ color: T.txt, fontWeight: 400 }}>{display}</span></div>;
            }
            if (f.type === "select") {
              return <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: 11, borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
                <span style={{ color: T.txt, opacity: 0.4 }}>{f.label}</span>
                <Select value={deal[f.id] || ""} onChange={e => store.updateDeal(deal.id, { [f.id]: e.target.value })} style={{ width: 120, borderRadius: 8 }}>
                  <option value="">--</option>{(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              </div>;
            }
            return <FieldEditor key={f.id} label={f.label} value={deal[f.id] || ""} type={f.type || "text"} options={f.options || []} onSave={v => store.updateDeal(deal.id, { [f.id]: f.type === "number" || f.type === "currency" ? Number(v) : v })} />;
          })}

          {/* Quick actions */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
              {[["Note", "edit"], ["Email", "mail"], ["Call", "phone"], ["Task", "check"], ["Meeting", "users"]].map(([l, ic]) => (
                <button key={l} onClick={() => { setQuickAction(quickAction === l ? null : l); setQaText(""); }}
                  style={{ padding: "8px 4px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: quickAction === l ? T.blue : "rgba(255,255,255,0.04)", color: quickAction === l ? "#D9DBDD" : T.txt, fontSize: 10, cursor: "pointer", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, fontFamily: "inherit" }}>
                  <Icon name={ic} size={14} color={quickAction === l ? "#D9DBDD" : T.txt} /><span>{l}</span>
                </button>
              ))}
            </div>
            {quickAction && (
              <div style={{ marginTop: 8, padding: 10, background: "#2C2E33", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)" }}>
                <textarea value={qaText} onChange={e => setQaText(e.target.value)} autoFocus
                  onKeyDown={e => { if (e.key === "Enter" && e.metaKey) submitQuickAction(); }}
                  placeholder={quickAction + " details..."}
                  style={{ width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "8px 10px", color: T.txt, fontSize: 11, outline: "none", fontFamily: "inherit", resize: "none", height: 50, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                  <button onClick={() => setQuickAction(null)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                  <button onClick={submitQuickAction} style={{ padding: "5px 12px", borderRadius: 8, border: "none", background: T.blue, color: "#D9DBDD", fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit", opacity: qaText.trim() ? 1 : 0.4 }}>Save</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CENTER - Timeline */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ display: "flex", gap: 0, borderBottom: "1px solid rgba(217,219,221,0.08)", padding: "0 16px" }}>
            {["Activity", "Notes", "Emails", "Calls", "Tasks", "Meetings"].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab.toLowerCase())} style={{
                padding: "10px 14px", border: "none", cursor: "pointer", background: "transparent", fontFamily: "inherit",
                borderBottom: activeTab === tab.toLowerCase() ? "2px solid " + T.blue : "2px solid transparent",
                color: activeTab === tab.toLowerCase() ? T.txt : T.txt, opacity: activeTab === tab.toLowerCase() ? 1 : 0.4, fontSize: 11, fontWeight: 400,
              }}>{tab}</button>
            ))}
          </div>

          <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..."
              onKeyDown={e => { if (e.key === "Enter" && e.metaKey) saveNote(); }}
              style={{ width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "10px 12px", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit", resize: "none", height: 60, boxSizing: "border-box" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <button onClick={saveNote} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: T.blue, color: "#D9DBDD", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit", textTransform: "uppercase", letterSpacing: ".04em", opacity: noteText.trim() ? 1 : 0.4 }}>Save Note</button>
            </div>
          </div>

          <div style={{ flex: 1, overflow: "auto", padding: "8px 16px" }}>
            {activities.filter(a => activeTab === "activity" || a.type === tabFilter[activeTab]).map((a, i) => {
              // Edit/delete affordance — see ContactRecord for full doc.
              // Lifecycle entries are delete-only via the modal's read-only mode.
              const editableTypes = ["note", "meeting", "call", "email", "lifecycle"];
              const isAdmin = auth.currentUser?.role === "admin";
              const isEditable = editableTypes.includes(a.type) && (
                a.type === "lifecycle"
                  ? true
                  : (a.by && a.by !== "System" && (a.by === store.currentUserId || isAdmin))
              );
              return (
                <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name={a.type === "call" ? "phone" : a.type === "email" ? "mail" : a.type === "meeting" ? "users" : a.type === "lifecycle" ? "refresh" : "edit"} size={12} color={T.txt} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>{a.type}</span>
                        <span style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{a.title}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                        <span style={{ fontSize: 10, color: T.txt, opacity: 0.4 }}>{a.date}</span>
                        {isEditable && (
                          <button onClick={() => setEditingActivity(a)} title="Edit"
                            style={{ background: "transparent", border: "none", padding: 4, cursor: "pointer", opacity: 0.5, display: "flex", alignItems: "center" }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                            onMouseLeave={e => e.currentTarget.style.opacity = "0.5"}>
                            <Icon name="edit" size={12} color={T.txt} />
                          </button>
                        )}
                      </div>
                    </div>
                    {a.notes && <div style={{ fontSize: 11, color: T.txt, opacity: 0.7, marginTop: 3, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{a.notes}</div>}
                  </div>
                </div>
              );
            })}
            {activities.filter(a => activeTab === "activity" || a.type === tabFilter[activeTab]).length === 0 && (
              <div style={{ textAlign: "center", padding: 30, color: T.txt, opacity: 0.4, fontSize: 12 }}>No {activeTab} yet</div>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ width: isMobile ? "100%" : 300, flexShrink: 0, borderLeft: isMobile ? "none" : "1px solid rgba(217,219,221,0.08)", borderTop: isMobile ? "1px solid rgba(217,219,221,0.08)" : "none", overflowY: isMobile ? "visible" : "auto", padding: 14 }}>

          {/* Contacts */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 400, color: T.txt, letterSpacing: ".06em", textTransform: "uppercase" }}>Contacts ({dealContactRows.length})</span>
              <button onClick={() => setShowAddContact(true)} style={{ padding: "4px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>
            </div>
            {dealContactRows.map(({ contact: ct, is_primary }) => {
              const hovered = hoveredContactId === ct.id;
              return (
                <div key={ct.id}
                  onClick={() => onSelectContact && onSelectContact(ct.id)}
                  onMouseEnter={() => setHoveredContactId(ct.id)}
                  onMouseLeave={() => setHoveredContactId(null)}
                  style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid " + (hovered ? "rgba(217,219,221,0.2)" : "rgba(217,219,221,0.08)"), cursor: "pointer", marginBottom: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{ct.fn} {ct.ln}</div>
                      <div style={{ fontSize: 10, color: T.txt, opacity: 0.5 }}>{ct.title}</div>
                      <div style={{ fontSize: 10, color: T.txt, opacity: 0.4, marginTop: 3 }}>{ct.email}</div>
                    </div>
                    {is_primary && (
                      <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: T.blue + "22", color: T.blue, fontWeight: 500, letterSpacing: ".04em", textTransform: "uppercase", flexShrink: 0, whiteSpace: "nowrap" }}>Primary</span>
                    )}
                  </div>
                  {hovered && (
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      {!is_primary && (
                        <button
                          onClick={async (e) => { e.stopPropagation(); await store.setPrimaryContact(deal.id, ct.id); }}
                          style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                        >Make primary</button>
                      )}
                      <button
                        onClick={async (e) => { e.stopPropagation(); await store.removeContactFromDeal(deal.id, ct.id); }}
                        style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid rgba(229,57,53,0.3)", background: "rgba(229,57,53,0.08)", color: "#E53935", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                      >Remove</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Company */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 400, color: T.txt, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 }}>Company</div>
            <div style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)" }}>
              <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{deal.co}</div>
              {contact && <div style={{ marginTop: 4, fontSize: 10, color: T.txt, opacity: 0.5 }}>{contact.industry} · {contact.city}, {contact.state}</div>}
            </div>
          </div>

          {/* Related Deals */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 400, color: T.txt, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 }}>Other Deals ({relatedDeals.length})</div>
            {relatedDeals.length === 0 ? <div style={{ fontSize: 11, color: T.txt, opacity: 0.4 }}>None</div> : relatedDeals.map(rd => (
              <div key={rd.id} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 400, color: T.txt }}>{rd.name}</div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, fontSize: 10 }}>
                  <span style={{ color: "#2E8B57", fontWeight: 400 }}>{fmtDollar(rd.val)}</span>
                  <span style={{ color: T.txt, opacity: 0.5 }}>{rd.stage}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Tasks */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 400, color: T.txt, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 }}>Tasks ({tasks.length})</div>
            {tasks.map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
                <div style={{ width: 20, height: 20, borderRadius: 8, background: t.done ? "#4059AD" : "transparent", border: t.done ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2, flexShrink: 0 }}>
                  {t.done && <span style={{ color: "#fff", fontSize: 9, fontWeight: 400 }}>✓</span>}
                </div>
                <div>
                  <div style={{ fontSize: 11, color: T.txt, textDecoration: t.done ? "line-through" : "none", opacity: t.done ? 0.4 : 1 }}>{t.title}</div>
                  <div style={{ fontSize: 9, color: T.txt, opacity: 0.4, marginTop: 1 }}>Due: {t.due}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddContact && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowAddContact(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 420, maxHeight: "70vh", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Add Contact to Deal</span>
              <button onClick={() => setShowAddContact(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.txt }}>×</button>
            </div>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
              <input
                autoFocus
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search contacts..."
                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
              <button
                onClick={() => { setShowAddContact(false); setShowCreateContactModal(true); }}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px dashed rgba(217,219,221,0.2)", background: "transparent", color: T.txt, fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}
              >+ Create new contact</button>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
              {(() => {
                const q = pickerSearch.trim().toLowerCase();
                const filtered = q
                  ? availableContacts.filter(c => {
                      const hay = [c.fn, c.ln, c.email, c.title, c.co].filter(Boolean).join(" ").toLowerCase();
                      return hay.includes(q);
                    })
                  : availableContacts;
                if (filtered.length === 0) {
                  return (
                    <div style={{ textAlign: "center", padding: 20, color: T.txt, opacity: 0.4, fontSize: 12 }}>
                      {q ? "No contacts match \"" + pickerSearch + "\"" : "All contacts already associated"}
                    </div>
                  );
                }
                return filtered.map(ct => (
                  <div key={ct.id} onClick={async () => { await store.addContactToDeal(deal.id, ct.id); setShowAddContact(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", marginBottom: 4, cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(217,219,221,0.2)"} onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(217,219,221,0.08)"}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{ct.fn} {ct.ln}</div>
                      <div style={{ fontSize: 10, color: T.txt, opacity: 0.5 }}>{ct.title} at {ct.co}</div>
                    </div>
                    <span style={{ fontSize: 10, color: "#FAFAFA", fontWeight: 400 }}>+ Add</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      <AddContactModal
        isOpen={showCreateContactModal}
        onClose={() => setShowCreateContactModal(false)}
        prefillCompany={deal.co}
        prefillDealId={deal.id}
        onCreated={async (newContact) => {
          if (!newContact) return;
          await store.addContactToDeal(deal.id, newContact.id);
        }}
      />

      {/* ═══ DELETE DEAL CONFIRMATION ═══ */}
      {showDeleteModal && (
        <div onClick={() => !deleteBusy && setShowDeleteModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: "92vw", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(229,57,53,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "#E53935", fontSize: 14, fontWeight: 500 }}>!</div>
              <div style={{ fontSize: 15, fontWeight: 400, color: T.txt }}>Delete {deal.name || "this deal"}?</div>
            </div>
            <div style={{ fontSize: 12, color: T.tx2, lineHeight: 1.6, marginBottom: 16 }}>
              This permanently removes the deal and its contact associations. Activities, emails, and calls will be kept with their deal link cleared. This cannot be undone.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setShowDeleteModal(false)} disabled={deleteBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={confirmDeleteDeal} disabled={deleteBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #E53935", background: "#E53935", color: "#FAFAFA", fontSize: 11, cursor: deleteBusy ? "default" : "pointer", fontFamily: "inherit", opacity: deleteBusy ? 0.6 : 1 }}>{deleteBusy ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Stage Gate Note Modal */}
      {stageNoteModal && ReactDOM.createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setStageNoteModal(null)}>
          <div style={{ background: "#2C2E33", borderRadius: 12, padding: 24, width: 420, maxWidth: "90vw", border: "1px solid rgba(250,250,250,0.08)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Stage Change Note Required</div>
            <div style={{ fontSize: 12, color: "#a0a3ab", marginBottom: 16 }}>
              Add a note explaining why this deal is moving to <span style={{ color: "#FFB547" }}>{stageNoteModal.newStage}</span>
            </div>
            <textarea
              value={stageNoteText}
              onChange={e => setStageNoteText(e.target.value)}
              placeholder="Why is this deal moving to this stage?"
              autoFocus
              rows={3}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(250,250,250,0.1)", background: "rgba(250,250,250,0.04)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", outline: "none" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={() => setStageNoteModal(null)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(250,250,250,0.1)", background: "transparent", color: "#a0a3ab", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={submitStageNote} disabled={!stageNoteText.trim()}
                style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: stageNoteText.trim() ? "#4059AD" : "#535C6E", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: stageNoteText.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                Move Deal
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <EditActivityModal
        activity={editingActivity}
        onSave={async (updates) => editingActivity ? await store.updateActivity(editingActivity.id, updates) : false}
        onDelete={async () => editingActivity ? await store.deleteActivity(editingActivity.id) : false}
        onClose={() => setEditingActivity(null)}
      />
    </div>
  );
}
